import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import Chord from "./NoteBar/Chord";
import { INoteBar } from "./NoteBar";
import { romanizeChars } from "../../utils/romanize";
import { useLyricScript } from "./useLyricScript";

/**
 * 가사 보기: 마디선 없이 가사 위에 코드만 올려 보여준다 (2026-10).
 * - 악보 데이터의 가사 문자열은 글자의 '칸 위치 = 박자 위치'(한 마디 36칸, to_sheets.py)라서,
 *   코드가 바뀌는 박(4칸 중 몇 번째)에 가장 가까운 음절을 찾아 그 음절 바로 위에 코드를 올린다.
 * - 박자 맞춤용 공백은 지우고, 진짜 띄어쓰기는 NBSP( )로 표시된 곳만 띄운다 (lyric_spacing.py 가 넣음).
 *   띄어쓰기 정보가 없는 악보는 코드가 바뀌는 곳에서만 끊어 보여준다.
 */
interface ILyricsView {
  bars: INoteBar[];
  barsPerLine: number;
  progress: number; // NotePlayer 의 currentProgress (마디당 100)
  isPlaying: boolean;
  allChordCode: any[];
  stopPlay: () => void;
  onClickBar: (threshold: number) => void;
  beatsPerBar?: number; // 한 마디 박 수 (4/4 → 4, 3/4 → 3). 코드 옆 '몇 박' 표시용
  stepMs?: number; // 진행이 25(한 박) 나아가는 데 걸리는 시간(ms) — 노래방식 색 채우기를 박 사이에서도 부드럽게
}

const NBSP = " ";
const COLS = 36; // to_sheets.py LYRIC_COLS
const ATTACH = 0.25; // 코드 위치에서 1박 안에 음절이 있으면 그 음절 위에, 없으면 코드만 따로

interface Syl {
  text: string; // 음절 1개 (코드만 있는 칸이면 "")
  chord: string;
  bar: number;
  wordEnd: boolean; // 이 음절 뒤에서 띄어쓰기
  beats?: number; // 이 코드를 치는 박 수 (다음 코드가 나올 때까지)
  meter?: string; // 이 마디부터 박자가 바뀜 ('2/4') — 마디 첫 음절에만
  at?: number; // 이 코드가 시작하는 진행 값 (마디당 100, 한 박 25)
  lead?: number; // 코드만 있는 칸: 이 코드 뒤 첫 음절까지 쉬는 박 수 (1박 이상일 때만)
}

const LEAD_EM = 0.9; // 쉬는 1박을 화면에서 띄우는 폭 (em)

// 코드 칩 폭(가사 글자 수 단위, 대략): 'Eᵇ'≈2, 'B♭7'≈2.6, 'Cm7(ᵇ5)'≈4
const chipChars = (html: string, beats?: number) => {
  const t = html.replace(/<[^>]+>/g, "");
  return 1.2 + t.length * 0.55 + (beats ? 0.4 + Math.min(8, Math.round(beats)) * 0.45 : 0);
};

/** 코드마다 몇 박 동안 치는지: 한 마디 4칸(칸 = 4분음표 한 박) 중 그 칸부터 다음 코드가 나오는 칸까지 */
const chordBeats = (bars: INoteBar[], beatsPerBar: number): number[][] => {
  const slots: [number, number][] = [];
  bars.forEach((b, i) => b.chords.forEach((c, k) => c && c.trim() && slots.push([i, k])));
  const out: number[][] = bars.map(() => [0, 0, 0, 0]);
  // 칸 하나의 박 수 (박자가 바뀐 마디는 그 마디 길이에 맞춤)
  const unitAt = (bar: number) => (beatsPerBar * (bars[bar]?.scale || 1)) / 4;
  slots.forEach(([i, k], n) => {
    const next = slots[n + 1];
    const end = next ? next[0] * 4 + next[1] : bars.length * 4;
    let sum = 0;
    for (let x = i * 4 + k; x < end; x++) sum += unitAt(Math.floor(x / 4));
    out[i][k] = Math.round(sum * 2) / 2;
  });
  return out;
};

// 박 수를 점으로: 한 박 = 점 하나, 지금 치는 코드는 지난 박만큼 점이 켜진다 (많으면 8개까지)
const BeatDots: React.FC<{ beats?: number; lit: number; hot: boolean }> = ({ beats, lit, hot }) => {
  const n = Math.min(8, Math.round(beats || 0));
  if (n < 1) return null;
  return (
    <Dots hot={hot} aria-label={`${beats}박`}>
      {Array.from({ length: n }, (_, i) => (
        <i key={i} className={hot && i < lit ? "on" : ""} />
      ))}
    </Dots>
  );
};

const barSyllables = (bar: INoteBar, barIndex: number, beats: number[]): Syl[] => {
  const str = bar.lyrics || "";
  const chars = [...str];
  const scale = Math.max(COLS, chars.length);
  const syls: (Syl & { pos: number })[] = [];
  chars.forEach((ch, i) => {
    if (ch === NBSP) {
      if (syls.length) syls[syls.length - 1].wordEnd = true;
      return;
    }
    if (/\s/.test(ch)) return;
    syls.push({ text: ch, chord: "", bar: barIndex, wordEnd: false, pos: i / scale });
  });
  bar.chords.forEach((c, k) => {
    if (!c || !c.trim()) return;
    const at = k / 4;
    const idx = syls.findIndex((s) => s.text && !s.chord && s.pos >= at - 0.06);
    if (idx >= 0 && syls[idx].pos - at <= ATTACH) {
      syls[idx].chord = c;
      syls[idx].beats = beats[k];
      syls[idx].at = barIndex * 100 + k * 25;
    } else {
      // 음절이 없는 박(길게 끄는 음·쉼표)에서 바뀌는 코드: 그 박 위치 순서대로 코드만 둔다
      let insertAt = syls.findIndex((s) => s.pos > at);
      if (insertAt < 0) insertAt = syls.length;
      // 코드를 치고 1박 넘게 쉬었다가 노래가 시작하면, 그만큼 띄워서 첫 음절이 실제 박 위치 쯤에 오게 (2026-10-07 사장님)
      const nextSyl = syls[insertAt];
      const gap = nextSyl && nextSyl.text ? (nextSyl.pos - at) * 4 : 0;
      const lead = gap >= 1 ? Math.min(3, Math.round(gap * 2) / 2) : undefined;
      syls.splice(insertAt, 0, { text: "", chord: c, bar: barIndex, wordEnd: false, pos: at, beats: beats[k], at: barIndex * 100 + k * 25, lead });
    }
  });
  if (bar.meter) {
    if (syls.length) syls[0].meter = bar.meter;
    else syls.push({ text: "", chord: "", bar: barIndex, wordEnd: false, pos: 0, meter: bar.meter });
  }
  return syls;
};

const LyricsView: React.FC<ILyricsView> = ({ bars, barsPerLine, progress, isPlaying, allChordCode, stopPlay, onClickBar, stepMs = 500, beatsPerBar = 4 }) => {
  const hasSpacing = useMemo(() => bars.some((b) => (b.lyrics || "").includes(NBSP)), [bars]);

  const lines = useMemo(() => {
    const dur = chordBeats(bars, beatsPerBar);
    const out: { words: Syl[][]; firstBar: number; lastBar: number }[] = [];
    for (let i = 0; i < bars.length; i += barsPerLine) {
      const slice = bars.slice(i, i + barsPerLine);
      const syls = slice.flatMap((b, k) => barSyllables(b, i + k, dur[i + k]));
      if (!syls.length) continue;
      // 단어 묶기: 띄어쓰기 정보가 있으면 그대로, 없으면 코드가 붙은 음절 앞에서 끊는다
      const words: Syl[][] = [];
      let cur: Syl[] = [];
      syls.forEach((s) => {
        if (!hasSpacing && s.chord && cur.length) {
          words.push(cur);
          cur = [];
        }
        cur.push(s);
        if (hasSpacing && s.wordEnd) {
          words.push(cur);
          cur = [];
        }
      });
      if (cur.length) words.push(cur);
      // 코드 칩은 음절 위에 떠 있으므로(폭을 차지하지 않음), 다음 코드 칩과 겹칠 만큼 가까우면 그 사이를 벌린다
      const flat = words.flatMap((w, wi) => w.map((s, si) => ({ s, gapAfter: si === w.length - 1 ? 1 : 0 })));
      flat.forEach((f, k) => {
        if (!f.s.chord) return;
        let room = 0; // 다음 코드까지의 대략 글자 수
        for (let j = k + 1; j < flat.length; j++) {
          if (flat[j].s.chord) {
            const need = Math.max(0, chipChars(f.s.chord, f.s.beats) - room - (f.s.lead || 0) * LEAD_EM);
            (f.s as any).pad = need;
            return;
          }
          room += (flat[j].s.text ? 1 : 0.4 + (flat[j].s.lead || 0) * LEAD_EM) + flat[j - 1].gapAfter * 0.45;
        }
      });
      // 노래방식 색 채우기: 음절마다 부르는 구간(진행 값, 마디당 100). 다음 음절이 시작할 때까지, 길어도 2박
      const sung = words.flat().filter((s) => s.text);
      sung.forEach((s: any, k) => {
        s.start = s.bar * 100 + s.pos * 100;
        const next: any = sung[k + 1];
        const limit = next ? next.bar * 100 + next.pos * 100 : (i + slice.length) * 100;
        s.end = Math.max(s.start + 6, Math.min(limit, s.start + 50));
      });
      out.push({ words, firstBar: i, lastBar: i + slice.length - 1 });
    }
    return out;
  }, [bars, barsPerLine, hasSpacing, beatsPerBar]);

  const currentBar = Math.floor(progress / 100);
  // 지금 울리는 코드: 시작 위치가 지금 진행 값 이하인 코드 중 가장 늦은 것
  const activeAt = useMemo(() => {
    let best = -1;
    lines.forEach((l) => l.words.forEach((w) => w.forEach((s) => {
      if (s.chord && s.at !== undefined && s.at <= progress && s.at > best) best = s.at;
    })));
    return best;
  }, [lines, progress]);
  const beatUnit = 25 * (4 / beatsPerBar); // 진행 값으로 한 박
  const litOf = (at?: number) => (at === undefined ? 0 : Math.floor((progress - at) / beatUnit) + 1);
  const currentLine = lines.findIndex((l) => currentBar >= l.firstBar && currentBar <= l.lastBar);
  const lineRefs = useRef<(HTMLDivElement | null)[]>([]);

  // 노래방처럼 지금 줄 가사를 재생 위치에 맞춰 한 글자씩 색칠. 진행 값은 한 박(25)마다 바뀌므로
  // 그 사이는 지난 시간으로 이어서 부드럽게 채운다 (화면 전체를 다시 그리지 않고 글자 스타일만 바꿈)
  const base = useRef({ p: progress, t: performance.now() });
  useEffect(() => {
    base.current = { p: progress, t: performance.now() };
  }, [progress]);
  const painted = useRef<number>(-1);
  useEffect(() => {
    const paint = () => {
      const b = base.current;
      const p = isPlaying ? b.p + Math.min(25, ((performance.now() - b.t) / Math.max(50, stepMs)) * 25) : b.p;
      if (painted.current >= 0 && painted.current !== currentLine) {
        lineRefs.current[painted.current]?.querySelectorAll<HTMLElement>("[data-ks]").forEach((el) => el.style.removeProperty("--f"));
      }
      painted.current = currentLine;
      const el = lineRefs.current[currentLine];
      if (!el) return;
      el.querySelectorAll<HTMLElement>("[data-ks]").forEach((t) => {
        const s = Number(t.dataset.s);
        const e = Number(t.dataset.e);
        const f = Math.max(0, Math.min(1, (p - s) / (e - s)));
        t.style.setProperty("--f", f.toFixed(3));
      });
    };
    paint();
    if (!isPlaying) return;
    let raf = 0;
    const loop = () => {
      paint();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying, currentLine, stepMs, lines]);

  // 줄 오른쪽 끝을 넘는 코드 칩은 안쪽으로 당긴다 (잘려 보이지 않게). 화면 폭이 바뀌면 다시
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const on = () => setWidth(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  useLayoutEffect(() => {
    const box = wrapRef.current;
    if (!box) return;
    const right = box.getBoundingClientRect().right;
    const chips = Array.from(box.querySelectorAll<HTMLElement>("[data-chip]"));
    const lineEls = Array.from(box.querySelectorAll<HTMLElement>("[data-line]"));
    lineEls.forEach((l) => (l.style.paddingRight = ""));
    chips.forEach((el) => {
      el.style.left = "";
      if (el.parentElement) el.parentElement.style.marginLeft = "";
    });
    // 같은 줄에서 앞 코드 칩과 겹치면 그 음절을 오른쪽으로 민다 (실제 그려진 폭으로 계산)
    const push = () => {
      for (let pass = 0; pass < 4; pass++) {
        let moved = false;
        let prev: DOMRect | null = null;
        chips.forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.width === 0) return;
          if (prev && Math.abs(prev.top - r.top) < 4 && r.left < prev.right + 4) {
            const host = el.parentElement as HTMLElement;
            host.style.marginLeft = `${parseFloat(host.style.marginLeft || "0") + prev.right + 4 - r.left}px`;
            moved = true;
            prev = el.getBoundingClientRect();
          } else prev = r;
        });
        if (!moved) break;
      }
    };
    push();
    // 줄 오른쪽 끝을 넘는 칩: 그 줄의 오른쪽 여백을 늘려 음절이 다음 줄로 넘어가게 (안으로 당기면 앞 칩과 겹침)
    for (let round = 0; round < 6; round++) {
      let changed = false;
      chips.forEach((el) => {
        const over = el.getBoundingClientRect().right - right;
        const line = el.closest("[data-line]") as HTMLElement | null;
        if (over > 0 && line) {
          const cur = parseFloat(line.style.paddingRight || "10");
          if (cur < 150) {
            line.style.paddingRight = `${Math.min(150, cur + over + 2)}px`;
            changed = true;
          }
        }
      });
      if (!changed) break;
      chips.forEach((el) => el.parentElement && (el.parentElement.style.marginLeft = ""));
      push();
    }
    chips.forEach((el) => {
      const over = el.getBoundingClientRect().right - right;
      if (over > 0) el.style.left = `${-2 - over}px`;
    });
  }, [lines, width]);

  // 자동 스크롤 중에는 지금 줄이 화면 위쪽 1/3 지점에 오게
  useEffect(() => {
    if (!isPlaying || currentLine < 0) return;
    const el = lineRefs.current[currentLine];
    const box = document.getElementById("note-wrap");
    if (!el || !box) return;
    const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
    box.scrollTop = Math.max(0, top - box.clientHeight / 3);
  }, [currentLine, isPlaying]);

  // 글로벌(영어권): 가사 로마자 (한글 / 로마자 / 둘 다). 줄마다 앞뒤 음절을 보고 소리대로 바꾼다
  const script = useLyricScript();
  const romOf = useMemo(() => {
    const m = new Map<any, string>();
    if (script === "ko") return m;
    lines.forEach((line) => {
      const seq: any[] = [];
      line.words.forEach((w, wi) => {
        if (wi) seq.push(" ");
        w.forEach((x) => x.text && seq.push(x));
      });
      const r = romanizeChars(seq.map((x) => (typeof x === "string" ? x : x.text)));
      seq.forEach((x, i) => typeof x !== "string" && m.set(x, r[i]));
    });
    return m;
  }, [lines, script]);

  return (
    <Wrap ref={wrapRef} data-nt="">
      {lines.map((line, li) => {
        const state = li < currentLine ? "past" : li === currentLine ? "now" : "next";
        return (
          <Line key={li} data-line="" ref={(el) => (lineRefs.current[li] = el)} state={state} instrumental={!line.words.some((w) => w.some((x) => x.text))}>
            {!line.words.some((w) => w.some((x) => x.text)) ? (
              // 가사 없는 줄(전주·간주): 코드만 한 줄로
              line.words.flat().map((x, xi) => (
                <React.Fragment key={xi}>
                {x.meter && <MeterTag title={/\//.test(x.meter) ? `이 마디는 ${x.meter}박자` : x.meter}>{x.meter}</MeterTag>}
                {x.chord ? (
                <InstChord hot={x.at === activeAt} onClick={() => onClickBar(x.bar * 100)}>
                  <Chord chord={x.chord} allChordCode={allChordCode} stopPlay={stopPlay} />
                  <BeatDots beats={x.beats} hot={x.at === activeAt} lit={litOf(x.at)} />
                </InstChord>
                ) : null}
                </React.Fragment>
              ))
            ) : line.words.map((word, wi) => (
              <Word key={wi}>
                {word.map((s, si) => {
                  const hot = !!s.chord && s.at === activeAt;
                  return (
                    <React.Fragment key={si}>
                    {s.meter && <MeterTag title={/\//.test(s.meter) ? `이 마디는 ${s.meter}박자` : s.meter}>{s.meter}</MeterTag>}
                    <SylBox
                      onClick={() => onClickBar(s.bar * 100)}
                      chordOnly={!s.text}
                      style={{
                        ...((s as any).pad ? { marginRight: `${(s as any).pad * 0.95}em` } : {}),
                        ...(s.lead ? { width: `${s.lead * LEAD_EM}em` } : {}),
                      }}
                    >
                      <ChordSlot hot={hot} data-chip="">
                        {s.chord ? <Chord chord={s.chord} allChordCode={allChordCode} stopPlay={stopPlay} /> : null}
                        {s.chord ? <BeatDots beats={s.beats} hot={hot} lit={litOf(s.at)} /> : null}
                      </ChordSlot>
                      {s.text && script === "rom" ? (
                        <Text data-ks="" data-t={romOf.get(s) || s.text} data-s={(s as any).start} data-e={(s as any).end}>{romOf.get(s) || s.text}</Text>
                      ) : s.text ? (
                        <>
                          <Text data-ks="" data-t={s.text} data-s={(s as any).start} data-e={(s as any).end}>{s.text}</Text>
                          {script === "both" && <Rom>{romOf.get(s)}</Rom>}
                        </>
                      ) : s.lead ? (
                        <Rest title={`${s.lead}박 쉼`} />
                      ) : (
                        <Text>{" "}</Text>
                      )}
                    </SylBox>
                    </React.Fragment>
                  );
                })}
              </Word>
            ))}
          </Line>
        );
      })}
    </Wrap>
  );
};

export default LyricsView;

const Wrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 4px;
`;

const Line = styled.div<{ state: string; instrumental?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  row-gap: 40px;
  padding: ${({ instrumental }) => (instrumental ? "8px 10px" : "40px 10px 8px")};
  column-gap: ${({ instrumental }) => (instrumental ? "8px" : "0")};
  margin: 0 -10px;
  border-radius: 12px;
  background: ${({ state }) => (state === "now" ? "#151a28" : "transparent")};
  opacity: ${({ state }) => (state === "past" ? 0.7 : 1)};
  transition: background 0.2s, opacity 0.2s;
  /* 노래방 색 채우기는 지금 줄만 (글자마다 --f). 지난 줄은 밝은 회색 글자로 — 어둡게 칠하면 배경에 묻혀 안 보임 */
  --f: 0;
  --unsung: ${({ state }) => (state === "past" ? "#a3a9b5" : "#e9ebf0")};
`;

const Word = styled.span`
  display: inline-flex;
  align-items: flex-end;
  /* 띄어쓰기 없이 길게 이어진 가사('괜찮아-지나간')는 줄 폭을 넘으면 안에서 넘김 (오른쪽이 잘리지 않게) */
  flex-wrap: wrap;
  row-gap: 40px;
  max-width: 100%;
  white-space: nowrap;
  margin-right: 0.45em;
`;

const SylBox = styled.span<{ chordOnly: boolean }>`
  position: relative;
  display: inline-block;
  cursor: pointer;
  ${({ chordOnly }) => (chordOnly ? "width: 6px;" : "")}
`;

/* 운지법 창을 여는 기존 코드 칩(Chord)을 그대로 쓰되, 가사 보기에서는 칩 모양으로 */
const ChordSlot = styled.div<{ hot: boolean }>`
  /* 음절 바로 위에 떠 있는 코드 칩 (가사 간격에 영향 없음) */
  position: absolute;
  left: -2px;
  bottom: 100%;
  margin-bottom: 4px;
  display: flex;
  white-space: nowrap;
  &:empty {
    display: none;
  }

  & > div {
    width: auto !important;
    min-width: 0 !important;
    margin: 0 !important;
    flex: none !important;
    transform: none !important;
    zoom: 1 !important;
  }
  & > div > * {
    display: inline-flex;
    align-items: center;
    min-width: 34px;
    height: 30px;
    padding: 0 8px !important;
    border-radius: 8px;
    background: ${({ hot }) => (hot ? "#ffb547" : "#2a2416")};
    color: ${({ hot }) => (hot ? "#0d0e12" : "#ffb547")} !important;
    border: 0 !important;
    font-size: 15px !important;
    font-weight: 600;
    line-height: 30px;
    font-style: normal;
    box-sizing: border-box;
  }
`;

// 곡 중간에 박자가 바뀌는 마디 표시 ('2/4')
const MeterTag = styled.span`
  align-self: center;
  margin: 0 6px 4px 0;
  padding: 0 6px;
  height: 20px;
  line-height: 20px;
  border-radius: 6px;
  border: 1px solid #5b8cff;
  color: #8fb0ff;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

// 코드 옆 박 수 점 (4개마다 조금 띄움)
const Dots = styled.span<{ hot: boolean }>`
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 5px;
  & > i {
    width: 5px;
    height: 5px;
    border-radius: 3px;
    background: ${({ hot }) => (hot ? "#5c4a24" : "#3a3f4b")};
    transition: background 0.1s, transform 0.1s;
  }
  & > i:nth-of-type(4n + 5) {
    margin-left: 3px;
  }
  & > i.on {
    background: #ffb547;
    transform: scale(1.25);
  }
`;

const InstChord = styled.span<{ hot: boolean }>`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  & > div {
    width: auto !important;
    margin: 0 !important;
    transform: none !important;
  }
  & > div > * {
    display: inline-flex;
    align-items: center;
    height: 30px;
    padding: 0 10px !important;
    border-radius: 8px;
    background: ${({ hot }) => (hot ? "#ffb547" : "#1a1710")};
    color: ${({ hot }) => (hot ? "#0d0e12" : "#ffb547")} !important;
    border: 1px solid #3a3220 !important;
    font-size: 15px !important;
    font-weight: 600;
    font-style: normal;
  }
`;

// 노래가 늦게 시작하는 마디: 코드 뒤 쉬는 박만큼 옅은 점선
const Rest = styled.span`
  display: block;
  height: 1.4em;
  margin-right: 0.3em;
  font-size: 19px;
  background: radial-gradient(circle, #4a505c 1.5px, transparent 2px) left center / 0.45em 100% repeat-x;
`;

/* 한글 아래 로마자 (가사 '둘 다') */
const Rom = styled.span`
  display: block;
  margin-top: -2px;
  font-size: 11px;
  line-height: 1.2;
  color: #a3a9b5;
  white-space: nowrap;
`;

const Text = styled.span`
  font-size: 19px;
  line-height: 1.4;
  color: #e9ebf0;
  /* 노래방 색 채우기: 글자는 보통 글자색(흰색)으로, 부른 만큼만 위에 호박색 글자를 덮는다.
     (배경을 글자 모양으로 칠하는 방식은 삼성 인터넷 등 브라우저 '다크 모드'가 배경을 어둡게 바꿔 글자가 안 보였음) */
  &[data-ks] {
    position: relative;
    color: var(--unsung, #e9ebf0);
  }
  &[data-ks]::after {
    content: attr(data-t);
    position: absolute;
    left: 0;
    top: 0;
    width: calc(var(--f, 0) * 100%);
    overflow: hidden;
    color: #ffb547;
    white-space: nowrap;
    pointer-events: none;
  }
  white-space: pre-wrap;
  word-break: keep-all;
`;
